import get from 'lodash/get';
import * as p from './permissions';

const defaultEntry = { allowed_by_feature_flag: false, allowed_by_role: false };
const allowedEntry = { allowed_by_feature_flag: true, allowed_by_role: true };

const alwaysAllowed = () => ({
  read: allowedEntry,
  create: allowedEntry,
  update: allowedEntry,
  delete: allowedEntry,
  patch: allowedEntry
});

const entry = (permissions, resourcePath, action) => {
  const allowed = get(permissions, `${resourcePath}.${action}.allowed`);
  if (allowed === undefined) return defaultEntry;
  return { allowed_by_feature_flag: allowed, allowed_by_role: allowed };
};

const RESOURCE_BY_FEATURE = {
  [p.FEATURE_COMMENTS]: 'comments',
  [p.FEATURE_SCHEDULER]: 'agents_schedules',
  [p.FEATURE_ANALYTICS]: 'data_analysis',
  [p.FEATURE_SETTINGS_REPORTING]: 'settings',
  [p.FEATURE_SETTINGS_SMTP]: 'settings',
  [p.FEATURE_SETTINGS_LDAP]: 'settings',
  [p.FEATURE_SETTINGS_SAML]: 'settings'
};

const getFeaturePermission = (feature, permissions) => {
  const resource = RESOURCE_BY_FEATURE[feature] || feature;
  return {
    read: entry(permissions, resource, 'view'),
    create: entry(permissions, resource, 'create'),
    update: entry(permissions, resource, 'update'),
    delete: entry(permissions, resource, 'delete'),
    patch: entry(permissions, resource, 'update')
  };
};

const TICKETING_TOOL_BY_FEATURE = {
  [p.FEATURE_TICKETING_JIRA]: 'jira',
  [p.FEATURE_TICKETING_SERVICENOW]: 'servicenow',
  [p.FEATURE_TICKETING_GITLAB]: 'gitlab',
  [p.FEATURE_TICKETING_WHD]: 'whd',
  [p.FEATURE_TICKETING_SERVICE_DESK]: 'mesd'
};

const getTicketingPermission = (feature, permissions) => {
  const tool = TICKETING_TOOL_BY_FEATURE[feature];
  const allowed = entry(permissions, 'ticketing', tool);
  return {
    read: allowed, create: allowed, update: allowed, delete: allowed, patch: allowed
  };
};

const getSingleAction = (resource, action) => (permissions) => {
  const allowed = entry(permissions, resource, action);
  return {
    read: allowed, create: allowed, update: defaultEntry, delete: defaultEntry, patch: defaultEntry
  };
};

const mappedPermissions = (permissions) => ({
  [p.FEATURE_COMMENTS]: getFeaturePermission(p.FEATURE_COMMENTS, permissions),
  [p.FEATURE_VULNS]: getFeaturePermission(p.FEATURE_VULNS, permissions),
  [p.FEATURE_TAGS]: getFeaturePermission(p.FEATURE_TAGS, permissions),
  [p.FEATURE_2FA]: getFeaturePermission(p.FEATURE_2FA, permissions),
  [p.FEATURE_AGENTS]: getFeaturePermission(p.FEATURE_AGENTS, permissions),
  [p.FEATURE_CLOUD_AGENTS]: getFeaturePermission(p.FEATURE_CLOUD_AGENTS, permissions),
  [p.FEATURE_CUSTOM_FIELDS]: getFeaturePermission(p.FEATURE_CUSTOM_FIELDS, permissions),
  [p.FEATURE_HOSTS]: getFeaturePermission(p.FEATURE_HOSTS, permissions),
  [p.FEATURE_REPORTING]: getFeaturePermission(p.FEATURE_REPORTING, permissions),
  [p.FEATURE_SCHEDULER]: getFeaturePermission(p.FEATURE_SCHEDULER, permissions),
  [p.FEATURE_SETTINGS_REPORTING]: getFeaturePermission(p.FEATURE_SETTINGS_REPORTING, permissions),
  [p.FEATURE_SETTINGS_SMTP]: getFeaturePermission(p.FEATURE_SETTINGS_SMTP, permissions),
  [p.FEATURE_SETTINGS_LDAP]: getFeaturePermission(p.FEATURE_SETTINGS_LDAP, permissions),
  [p.FEATURE_SETTINGS_SAML]: getFeaturePermission(p.FEATURE_SETTINGS_SAML, permissions),
  [p.FEATURE_TICKETING_GITLAB]: getTicketingPermission(p.FEATURE_TICKETING_GITLAB, permissions),
  [p.FEATURE_TICKETING_JIRA]: getTicketingPermission(p.FEATURE_TICKETING_JIRA, permissions),
  [p.FEATURE_TICKETING_SERVICENOW]: getTicketingPermission(p.FEATURE_TICKETING_SERVICENOW, permissions),
  [p.FEATURE_TICKETING_WHD]: getTicketingPermission(p.FEATURE_TICKETING_WHD, permissions),
  [p.FEATURE_USERS]: getFeaturePermission(p.FEATURE_USERS, permissions),
  [p.FEATURE_VULN_TEMPLATE]: getFeaturePermission(p.FEATURE_VULN_TEMPLATE, permissions),
  [p.FEATURE_WORKSPACES]: getFeaturePermission(p.FEATURE_WORKSPACES, permissions),
  [p.FEATURE_PIPELINES]: getFeaturePermission(p.FEATURE_PIPELINES, permissions),
  [p.FEATURE_JOBS]: getFeaturePermission(p.FEATURE_JOBS, permissions),
  [p.FEATURE_DUPLICATES]: getFeaturePermission(p.FEATURE_DUPLICATES, permissions),
  [p.FEATURE_PROJECTS]: getFeaturePermission(p.FEATURE_PROJECTS, permissions),
  [p.FEATURE_TASKS]: getFeaturePermission(p.FEATURE_TASKS, permissions),
  [p.FEATURE_ANALYTICS]: getFeaturePermission(p.FEATURE_ANALYTICS, permissions),
  [p.FEATURE_GLOBAL_HOSTS]: getFeaturePermission(p.FEATURE_GLOBAL_HOSTS, permissions),
  [p.FEATURE_ADVANCED_NOTIFICATIONS]: getFeaturePermission(p.FEATURE_ADVANCED_NOTIFICATIONS, permissions),
  [p.FEATURE_SERVICES]: getFeaturePermission(p.FEATURE_SERVICES, permissions),
  [p.FEATURE_USER_TOKENS]: getFeaturePermission(p.FEATURE_USER_TOKENS, permissions),
  [p.FEATURE_TICKETING_SERVICE_DESK]: getTicketingPermission(p.FEATURE_TICKETING_SERVICE_DESK, permissions),
  [p.FEATURE_CREDENTIALS]: alwaysAllowed(),
  [p.FEATURE_BULK_CREATE]: getSingleAction('bulk_create', 'bulk_create')(permissions),
  [p.FEATURE_AGENT_TOKEN]: alwaysAllowed(),
  [p.FEATURE_ROLES]: getFeaturePermission(p.FEATURE_ROLES, permissions),
  [p.FEATURE_AGENT_RUN]: getSingleAction('agents', 'run')(permissions),
  [p.FEATURE_CLOUD_AGENT_RUN]: getSingleAction('cloud_agents', 'run')(permissions),
  [p.FEATURE_WEEKLY_REPORTS]: getFeaturePermission(p.FEATURE_WEEKLY_REPORTS, permissions),
  [p.FEATURE_MCP]: getFeaturePermission(p.FEATURE_MCP, permissions)
});

export default mappedPermissions;
